import { computed, observable, makeObservable } from "mobx";
import { inject, observer } from "mobx-react";
import { fromPromise, PENDING } from "mobx-utils";
import { Component } from "react";
import { FormattedMessage } from "react-intl";
import { compose } from "nvent-web/utils/compose";

import { SecondaryBlueButton } from "nvent-web/components/Button";
import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import Api from "nvent-web/services/Api";
import { ProductsStore } from "nvent-web/stores/Products";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { UserStore } from "nvent-web/stores/User";
import type {
  CategoryRecommendation,
  CategoryRecommendationVariant,
} from "nvent-web/types/ProductRecommendation";
import contactDataForCountries from "nvent-web/utils/contactDataForCountries";
import { createInitialFromPromise } from "nvent-web/utils/createInitialFromPromise";
import { recommendationToProductSpecs } from "nvent-web/utils/recommendationToProductSpecs";

import ProductRecommendationCard from "nvent-web/components/ProductRecommendationCard/ProductRecommendationCard";
import { getAncestryString } from "../../utils/getAncestryString";
import StepHeading from "../StepHeading/StepHeading";

import style from "./ProductRecommendationsStep.module.scss";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

interface ProjectParams {
  projectId: string;
  roomId: string;
}

interface ProductRecommendationsStepInnerProps
  extends ProjectParams, WithRouterProps {
  api: Api;
  user: UserStore;
  products: ProductsStore;
  rooms: RoomsStore;
  projects: ProjectsStore;
}

class ProductRecommendationsStep extends Component<ProductRecommendationsStepInnerProps> {
  private recommendationsPromise = createInitialFromPromise<
    CategoryRecommendation[]
  >([]);

  // Observable copies of props for use in computed properties
  private products: ProductsStore;
  private user: UserStore;
  private projectIdValue: number;
  private roomIdValue: number;

  constructor(props: ProductRecommendationsStepInnerProps) {
    super(props);

    // Initialize observable props
    this.products = props.products;
    this.user = props.user;
    this.projectIdValue = Number(props.params.projectId);
    this.roomIdValue = Number(props.params.roomId);

    makeObservable<
      ProductRecommendationsStep,
      | "recommendationsPromise"
      | "products"
      | "user"
      | "projectIdValue"
      | "roomIdValue"
    >(this, {
      recommendationsPromise: observable,
      products: observable.ref,
      user: observable.ref,
      projectIdValue: observable,
      roomIdValue: observable,
      isLoading: computed,
      recommendations: computed,
      customerSupportPhoneNumber: computed,
      projectId: computed,
      roomId: computed,
    });
  }

  componentDidUpdate(prevProps: ProductRecommendationsStepInnerProps) {
    // Update observable props when they change
    if (prevProps.products !== this.props.products) {
      this.products = this.props.products;
    }
    if (prevProps.user !== this.props.user) {
      this.user = this.props.user;
    }
    if (prevProps.params.projectId !== this.props.params.projectId) {
      this.projectIdValue = Number(this.props.params.projectId);
    }
    if (prevProps.params.roomId !== this.props.params.roomId) {
      this.roomIdValue = Number(this.props.params.roomId);
    }
  }

  get isLoading(): boolean {
    return this.recommendationsPromise.state === PENDING;
  }

  get recommendations(): CategoryRecommendation[] {
    return this.recommendationsPromise
      .case({
        pending: () => [],
        fulfilled: (value) => value,
        rejected: () => [],
      })
      .map((recommendation) => {
        const recommendationCategory = this.products.productCategories.find(
          ({ id }) => id === recommendation.productCategoryId
        );
        return {
          ...recommendation,
          featureDescription: recommendationCategory
            ? recommendationCategory.featureDescription
            : {},
        };
      });
  }

  get customerSupportPhoneNumber() {
    const { companyAddress } = this.user;
    const matchedContactData = contactDataForCountries.find(
      ({ countryCode }) =>
        countryCode === companyAddress.countryCode.toUpperCase()
    );
    return matchedContactData ? matchedContactData.phoneNumber : "";
  }

  get projectId(): number {
    return this.projectIdValue;
  }

  get roomId(): number {
    return this.roomIdValue;
  }

  componentDidMount() {
    this.recommendationsPromise = fromPromise(this.fetchRecommendations());
  }

  render() {
    if (this.isLoading) {
      return <LoadingSection />;
    }
    const { roomDetails } = this.props.rooms;
    const { projectDetails } = this.props.projects;

    const ancestryString = getAncestryString({
      projectName: projectDetails?.name,
      levels: roomDetails?.levels,
      roomName: roomDetails?.name,
    });

    if (this.recommendations && this.recommendations.length === 0) {
      return (
        <>
          <StepHeading step={3} ancestry={ancestryString} />
          <div className={style.noProducts}>
            <FormattedMessage id="selectionGuide.noProducts" />
            <br />
            <FormattedMessage id="selectionguide.noProducts.customerService" />
            <a
              className={style.phoneNumber}
              href={`tel:${this.customerSupportPhoneNumber}`}
            >
              {this.customerSupportPhoneNumber}
            </a>
          </div>
          <div className={style.noProductsPhoneNumber} />
        </>
      );
    }

    return (
      <>
        <StepHeading step={3} ancestry={ancestryString} />
        <div className={style.wrapper}>
          {this.recommendations &&
            this.recommendations.map(({ recommendations }) =>
              recommendations.map((recommendation) => (
                <ProductRecommendationCard
                  key={recommendation.productSkus[0].id}
                  recommendation={recommendation}
                  actions={
                    <SecondaryBlueButton
                      className={style.addButton}
                      onClick={() => this.onSelect(recommendation)}
                    >
                      <FormattedMessage id="actions.add" />
                    </SecondaryBlueButton>
                  }
                />
              ))
            )}
        </div>
      </>
    );
  }
  private onSelect = async (
    selectedRecommendation: CategoryRecommendationVariant
  ) => {
    const selectedProductSkus = recommendationToProductSpecs(
      selectedRecommendation
    );

    const { products, navigate } = this.props;
    await products.createProductSkus(
      this.projectId,
      this.roomId,
      selectedProductSkus
    );

    navigate(`/projects/${this.projectId}/rooms/${this.roomId}`);
  };
  private fetchRecommendations = async (): Promise<
    CategoryRecommendation[]
  > => {
    return (
      await this.props.api.products.getRecomendations(
        this.projectId,
        this.roomId
      )
    ).data;
  };
}

const ProductRecommendationsStepComponent = compose(
  inject("api", "products", "user", "rooms", "projects"),
  withRouter,
  observer
)(ProductRecommendationsStep);

ProductRecommendationsStepComponent.displayName = "ProductRecommendationsStep";

export default ProductRecommendationsStepComponent;
